import { useState, useEffect } from 'react';
import { connectAPI } from '../../services/api';
import { Plus, Edit3, Trash2, X, KeyRound, Copy, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const emptyForm = { name: '', website: '', logoUrl: '', redirectUris: '', isActive: true };

const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

// Super admin: register partner apps that can use "Connect with AINA Suite".
export default function AdminConnectApps() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, client = edit
  const [revealed, setRevealed] = useState(null); // { name, clientId, clientSecret } shown once

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data } = await connectAPI.listClients();
      setClients(data.data);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleRotate = async (client) => {
    if (!window.confirm(`Generate a new client secret for "${client.name}"? The old secret stops working immediately.`)) return;
    try {
      const { data } = await connectAPI.rotateClientSecret(client._id);
      setRevealed(data.data);
    } catch (_) {}
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete "${client.name}"? This disconnects all ${client.connectionCount} merchant connection(s) and cannot be undone.`)) return;
    try {
      await connectAPI.deleteClient(client._id);
      toast.success('App deleted');
      fetchClients();
    } catch (_) {}
  };

  const handleToggle = async (client) => {
    try {
      await connectAPI.updateClient(client._id, { isActive: !client.isActive });
      fetchClients();
    } catch (_) {}
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Connected Apps</h1>
          <p className="page-subtitle">Partner apps that can offer a "Connect with AINA Suite" button</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({})}><Plus size={16} /> New App</button>
      </div>

      <div className="page-body">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : clients.length === 0 ? (
          <div className="card empty-state" style={{ padding: 40, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)' }}>No partner apps yet. Create one to get a client ID and secret for a partner.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {clients.map(c => (
              <div key={c._id} className="card" style={{ padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <strong>{c.name}</strong>
                      <span className={`badge ${c.isActive ? 'badge-green' : 'badge-gray'}`}>{c.isActive ? 'Active' : 'Disabled'}</span>
                      <span className="badge badge-blue">{c.connectionCount} connected</span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Client ID: <code>{c.clientId}</code>
                      <button className="btn-icon" onClick={() => copy(c.clientId)} title="Copy client ID" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}><Copy size={13} /></button>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, wordBreak: 'break-all' }}>
                      Redirect URIs: {c.redirectUris.join(', ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleToggle(c)}>{c.isActive ? 'Disable' : 'Enable'}</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleRotate(c)} title="New client secret"><KeyRound size={14} /></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(c)} title="Edit"><Edit3 size={14} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c)} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ClientModal
          client={editing._id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(created) => { setEditing(null); fetchClients(); if (created) setRevealed(created); }}
        />
      )}
      {revealed && <SecretModal data={revealed} onClose={() => setRevealed(null)} />}
    </>
  );
}

function ClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState(client
    ? { name: client.name, website: client.website || '', logoUrl: client.logoUrl || '', redirectUris: client.redirectUris.join('\n'), isActive: client.isActive }
    : emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (client) {
        await connectAPI.updateClient(client._id, form);
        toast.success('App updated');
        onSaved(null);
      } else {
        const { data } = await connectAPI.createClient(form);
        onSaved(data.data);
      }
    } catch (_) {}
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 className="modal-title">{client ? 'Edit App' : 'New Partner App'}</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">App name *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g. ShopEasy" required />
              <div className="form-hint">Shown to merchants on the consent screen.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-input" value={form.website} onChange={set('website')} placeholder="https://partner.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Logo URL</label>
              <input className="form-input" value={form.logoUrl} onChange={set('logoUrl')} placeholder="https://partner.com/logo.png" />
            </div>
            <div className="form-group">
              <label className="form-label">Redirect URIs * (one per line)</label>
              <textarea className="form-input" rows={3} value={form.redirectUris} onChange={set('redirectUris')}
                placeholder="https://partner.com/aina/callback" required />
              <div className="form-hint">Must match the partner's redirect_uri exactly, including path.</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : client ? 'Save' : 'Create App'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SecretModal({ data, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 className="modal-title">Credentials for {data.name}</h3>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', color: '#b45309', fontSize: '0.82rem', marginBottom: 16 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            Copy the client secret now and send it to the partner securely. It won't be shown again.
          </div>
          {[['Client ID', data.clientId], ['Client secret', data.clientSecret]].map(([label, value]) => (
            <div className="form-group" key={label}>
              <label className="form-label">{label}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input" readOnly value={value} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
                <button className="btn btn-secondary" onClick={() => copy(value)}><Copy size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>I've saved it</button>
        </div>
      </div>
    </div>
  );
}
